import { useCallback, useMemo, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUserPrint } from '@app/hooks';
import { getAccessToken } from '@app/utils';

import type { IframePageProps } from '@pages/IframePage/types';
import { openBillingPayment } from '@pages/IframePage/utils/openBillingPayment';

export const useIframePage = (
  {
    host = '',
    jwt,
    withJwt = true,
    withContextInUrl = true,
    onMessage,
    organizationId,
    branchId,
    recordId,
    isEmbedded = false,
  }: IframePageProps,
  iframeRef: RefObject<HTMLIFrameElement>
) => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { activeBranchId } = useUserPrint();

  const crmAccessToken = getAccessToken() ?? '';
  const accessToken = withJwt ? jwt ?? crmAccessToken : '';

  const bookingHost = process.env.REACT_APP_BOOKING_HOST || '';
  const _host = host || bookingHost;

  const hostOrigin = useMemo(() => {
    try {
      return new URL(_host).origin;
    } catch {
      return '*';
    }
  }, [_host]);

  const [iframeUrl] = useState<string>(() => {
    try {
      const url = new URL(_host);

      if (accessToken) {
        url.searchParams.set('jwt', accessToken);
      }

      if (!withContextInUrl) return url.toString();

      if (organizationId != null) {
        url.searchParams.set('organizationId', String(organizationId));

        return url.toString();
      }

      const targetBranchId = branchId ?? activeBranchId;

      if (targetBranchId != null) {
        url.searchParams.set('branchId', String(targetBranchId));
      }

      if (recordId != null) {
        url.searchParams.set('recordId', String(recordId));
      }

      if (isEmbedded) {
        return url.toString();
      }

      const currentSearchParams = new URLSearchParams(window.location.search);

      const redirectTo = currentSearchParams.get('redirectTo');

      const redirect = redirectTo
        ? decodeURIComponent(redirectTo)
        : `${window.location.pathname}${window.location.search}${window.location.hash}`;

      url.searchParams.set('redirect', redirect);

      return url.toString();
    } catch {
      return '';
    }
  });

  const handleChildNavigation = useCallback(
    (event: MessageEvent) => {
      const frameWindow = iframeRef.current?.contentWindow;

      if (!frameWindow || event.source !== frameWindow) return;
      if (event.origin !== hostOrigin && hostOrigin !== '*') return;

      if (onMessage) {
        if (event.origin === hostOrigin) {
          onMessage(event.data, frameWindow);
        }
        return;
      }

      if (event.data?.type === 'byhelp.billing.open-payment') {
        if (event.origin === hostOrigin) {
          openBillingPayment(event.data.confirmationUrl);
        }
        return;
      }

      if (event.data?.type === 'ROUTE_CHANGE') {
        if (isEmbedded) return;

        const { path = '', search = '', hash = '' } = event.data;

        let finalUrl = `${path}${search}${hash}`;

        const currentSearchParams = new URLSearchParams(window.location.search);
        const savedRedirectTo = currentSearchParams.get('redirectTo');

        if (savedRedirectTo && !finalUrl.includes('redirectTo=')) {
          const separator = finalUrl.includes('?') ? '&' : '?';

          finalUrl += `${separator}redirectTo=${encodeURIComponent(
            savedRedirectTo
          )}`;
        }

        navigate(finalUrl, { replace: true });
      }
    },
    [navigate, hostOrigin, isEmbedded, iframeRef, onMessage]
  );

  return {
    handleChildNavigation,
    accessToken,
    setIsLoading,
    isLoading,
    hostOrigin,
    iframeUrl,
  };
};
